import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { StageChip } from '@/components/StageBadge'
import { fmtAgo } from '@/lib/format'
import { SCIENCE_PROGRAM, programLabel } from '@/lib/programs'
import { sourceTemplate } from '@/lib/scientific'
import { intakeStage, pipelineStage, taskDisplayStage, workflowStage } from '@/lib/stages'
import type { IntakeSummary } from '@/lib/templateIntakes'
import type { Task } from '@/lib/types'

/** What a list row has to carry: the developer list and the experts' directory project different fields. */
export type TreeTask = Pick<Task, 'task_id' | 'title' | 'stage' | 'updated_at'> & Partial<Omit<Task, 'authoring'>>
  & { authoring?: { status?: string } | null; author_name?: string }
type Member = { task_id: string; title: string; status: string }
type Node = {
  key: string; kind: 'template' | 'workflow' | 'task'; title: string; href: string
  /** Workflows in a template or tasks in a workflow, known before a workflow's tasks are loaded. */
  count: number; match: boolean; children: Node[]; task?: TreeTask; intake?: IntakeSummary; member?: Member
}

const science = (t: TreeTask) => !!t.collection || t.kind === 'scientific_workflow'
const updated = (n: Node) => n.task?.updated_at ?? n.intake?.updated_at ?? ''
// A task inside a workflow is at its workflow's point of the pipeline.
const MEMBER_STAGE: Record<string, string> = { quality_review: 'quality', needs_changes: 'quality', qc_passed: 'quality', published: 'published' }
const STAGE_HELP: Record<string, string> = {
  draft: 'The expert is preparing the data and notes.',
  research: 'The researcher agent is proposing workflows from the data.',
  workflow_review: 'An expert reads the proposals and approves them or sends them back.',
  authoring: 'The authoring agent builds the workflow and grades its tasks.',
  quality: 'Experts check the AI results; a developer publishes the workflow or sends it back.',
  published: 'Checked and published.',
}

/** One stage of the six-stage board: its name in that stage's colour, explained on hover. */
function StageTag({ stage }: { stage: string }) {
  const def = pipelineStage(stage)
  return def ? <span className={`chip stage-tag stage-${stage}`} title={`${def.label}: ${STAGE_HELP[stage]}`}>{def.label}</span> : null
}

function prune(nodes: Node[], inherited: boolean): Node[] {
  return nodes.flatMap(n => {
    const children = prune(n.children, inherited || n.match)
    return inherited || n.match || children.length ? [{ ...n, children }] : []
  })
}

/**
 * Templates hold the workflows proposed from them and a built workflow holds its tasks: one list,
 * like folders. Ordinary tasks are top-level rows. Clicking a folder row opens it; its name opens the
 * page. A workflow's tasks load when it is first opened, and the tree is grouped in one pass and
 * rebuilt only when the data or the filters change.
 */
export function WorkTree({ tasks, templates, matchTask, matchTemplate, taskHref, templateHref, memberHref, expert, decorate, loading, empty }: {
  tasks: TreeTask[]; templates: IntakeSummary[]
  matchTask: (t: TreeTask) => boolean; matchTemplate: (i: IntakeSummary) => boolean
  taskHref: (t: TreeTask) => string; templateHref: (i: IntakeSummary) => string; memberHref: (workflow: TreeTask, member: Member) => string
  /** The expert cell of a task or template row. */
  expert: (row: { task?: TreeTask; intake?: IntakeSummary }) => ReactNode
  /** Wraps a task's stage tag, e.g. with QC verdicts. */
  decorate?: (t: TreeTask, tag: ReactNode) => ReactNode
  loading: boolean; empty: string
}) {
  const navigate = useNavigate()
  const [members, setMembers] = useState<Record<string, Member[] | string>>({})
  const [toggled, setToggled] = useState<Set<string>>(new Set())

  const { shown, counts } = useMemo(() => {
    const taskNode = (t: TreeTask): Node => {
      const loaded = members[t.task_id]
      return { key: t.task_id, kind: science(t) ? 'workflow' : 'task', title: t.title, href: taskHref(t), task: t,
        count: t.collection?.task_ids.length ?? 0, match: matchTask(t),
        children: Array.isArray(loaded) ? loaded.map(member => ({ key: member.task_id, kind: 'task', title: member.title,
          href: memberHref(t, member), count: 0, match: false, children: [], member })) : [] }
    }
    const ids = new Set(templates.map(i => i.template_id))
    const byTemplate = new Map<string, TreeTask[]>()
    const loose: TreeTask[] = []
    for (const t of tasks) {
      const source = science(t) ? sourceTemplate(t as Task)?.template_id : undefined
      if (source && ids.has(source)) byTemplate.set(source, [...(byTemplate.get(source) ?? []), t])
      else loose.push(t)
    }
    const roots: Node[] = [
      ...templates.map(i => {
        const children = (byTemplate.get(i.template_id) ?? []).map(taskNode)
        return { key: i.template_id, kind: 'template' as const, title: i.title, href: templateHref(i), intake: i,
          count: children.length, children, match: matchTemplate(i) }
      }),
      ...loose.map(taskNode),
    ].sort((a, b) => updated(b).localeCompare(updated(a)))
    const shown = prune(roots, false)
    const counts = { templates: 0, workflows: 0, tasks: 0 }
    const walk = (nodes: Node[]) => nodes.forEach(n => {
      counts[`${n.kind}s` as keyof typeof counts] += 1
      if (n.kind === 'workflow') counts.tasks += n.count
      else walk(n.children)
    })
    walk(shown)
    return { shown, counts }
  }, [tasks, templates, members, matchTask, matchTemplate, taskHref, templateHref, memberHref])

  // Templates open by default and workflows closed.
  const isOpen = (n: Node) => (n.kind === 'template') !== toggled.has(n.key)
  function toggle(n: Node) {
    setToggled(current => { const next = new Set(current); if (next.has(n.key)) next.delete(n.key); else next.add(n.key); return next })
    const t = n.task
    if (t?.collection && !isOpen(n) && !Array.isArray(members[n.key])) {
      void api.get<{ tasks: Member[] }>(`/scientific/templates/${t.scientific_template_id ?? t.task_id}/members`)
        .then(result => setMembers(current => ({ ...current, [n.key]: result.tasks })))
        .catch(e => setMembers(current => ({ ...current, [n.key]: (e as Error).message })))
    }
  }

  function stageOf(n: Node): ReactNode {
    if (n.member) return <StageTag stage={MEMBER_STAGE[n.member.status] ?? 'authoring'} />
    if (n.intake) {
      const key = intakeStage(n.intake.status)
      return key ? <StageTag stage={key} />
        : <span className="chip stage-tag stage-approved" title="Its proposals were approved: their workflows are listed under it.">Proposals approved</span>
    }
    const t = n.task!
    if (t.lifecycle === 'retired') return <span className="chip retired">Retired</span>
    if (!science(t)) return <StageChip stage={taskDisplayStage(t as Task)} version={t.workflow_version} />
    return <StageTag stage={workflowStage(t)} />
  }

  const rowsOf = (nodes: Node[], depth: number, parent?: Node): ReactNode[] => nodes.flatMap(n => {
    const folder = n.count > 0
    const open = folder && isOpen(n)
    const noun = n.kind === 'template' ? 'workflow' : 'task'
    const tag = stageOf(n)
    return [<tr key={n.key} className={`clickable tree-row${open ? ' is-open' : ''}${depth ? ' is-nested' : ''}`}
      onClick={() => folder ? toggle(n) : navigate(n.href)}>
      <td><span className="tree-cell" style={{ '--depth': depth } as CSSProperties}>
        <Icon name={n.kind === 'template' ? 'template' : n.kind === 'workflow' ? 'layers' : 'file'} size={16} />
        <span className="tree-name">
          <Link to={n.href} onClick={event => event.stopPropagation()}>{n.title}</Link>
          <span className="tree-meta">
            {n.kind === 'template' ? 'Template' : n.kind === 'workflow' ? 'Workflow' : n.key}
            {folder && <button type="button" className="tree-count" aria-expanded={open}
              onClick={event => { event.stopPropagation(); toggle(n) }}>
              {n.count} {noun}{n.count === 1 ? '' : 's'}<Icon name="chevron" size={12} />
            </button>}
          </span>
        </span>
      </span></td>
      <td className="small" data-label="Program">{n.task ? programLabel(n.task as Task) : n.intake ? SCIENCE_PROGRAM : programLabel(parent!.task as Task)}</td>
      <td data-label="Stage">{n.task && decorate ? decorate(n.task, tag) : tag}</td>
      <td className="small" data-label="Expert">{n.member ? '' : expert({ task: n.task, intake: n.intake })}</td>
      <td className="muted small" data-label="Updated">{n.member ? '' : fmtAgo(updated(n))}</td>
    </tr>, ...(!open ? [] : n.kind === 'workflow' && !Array.isArray(members[n.key])
      ? [<tr key={`${n.key}:loading`} className="is-nested"><td colSpan={5} className="muted small">
          <span className="tree-cell" style={{ '--depth': depth + 1 } as CSSProperties}>
            {typeof members[n.key] === 'string' ? members[n.key] as string : 'Loading tasks…'}</span></td></tr>]
      : rowsOf(n.children, depth + 1, n))]
  })

  return <>
    <p className="count tree-count-line">{[[counts.templates, 'template'], [counts.workflows, 'workflow'], [counts.tasks, 'task']]
      .filter(([n]) => n).map(([n, noun]) => `${n} ${noun}${n === 1 ? '' : 's'}`).join(', ')}</p>
    <div className="table-wrap task-table tree-table"><table>
      <colgroup>
        <col className="title-column" /><col className="program-column" /><col className="stage-column" />
        <col className="expert-column" /><col className="updated-column" />
      </colgroup>
      <thead><tr><th>Name</th><th>Program</th><th>Stage</th><th>Expert</th><th>Updated</th></tr></thead>
      <tbody>
        {rowsOf(shown, 0)}
        {loading && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
        {!loading && !shown.length && <tr><td colSpan={5} className="muted">{empty}</td></tr>}
      </tbody>
    </table></div>
  </>
}
